import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { getManufacturerList, createManufacturer, updateManufacturer, deleteManufacturer } from '../../api/api';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import AdminPortalSideBar from '../../containers/AdminSideBarContainer/AdminSideBarContainer';
import { PageContainer, ContentContainer } from '../AdminPortalPage/AdminPortalPage.styles';

export default function ManufacturerManagementPage() {
  const [manufacturers, setManufacturers] = useState([]);
  const [filteredManufacturers, setFilteredManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingManufacturer, setEditingManufacturer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch manufacturers on component mount
  useEffect(() => {
    fetchManufacturers();
  }, []);

  // Filter manufacturers based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredManufacturers(manufacturers);
    } else {
      const filtered = manufacturers.filter(manufacturer =>
        manufacturer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (manufacturer.description && manufacturer.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredManufacturers(filtered);
    }
  }, [manufacturers, searchTerm]);

  const fetchManufacturers = async () => {
    try {
      setLoading(true);
      const data = await getManufacturerList();
      setManufacturers(data);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
      showSnackbar('Error fetching manufacturers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'success' });
  };

  const handleOpenDialog = (manufacturer = null) => {
    if (manufacturer) {
      setEditingManufacturer(manufacturer);
      setFormData({
        name: manufacturer.name,
        description: manufacturer.description || '',
        is_active: manufacturer.is_active
      });
    } else {
      setEditingManufacturer(null);
      setFormData({
        name: '',
        description: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingManufacturer(null);
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showSnackbar('Manufacturer name is required', 'error');
      return;
    }

    try {
      if (editingManufacturer) {
        await updateManufacturer(editingManufacturer.id, formData);
        showSnackbar('Manufacturer updated successfully');
      } else {
        await createManufacturer(formData);
        showSnackbar('Manufacturer created successfully');
      }
      
      handleCloseDialog();
      fetchManufacturers();
    } catch (error) {
      console.error('Error saving manufacturer:', error);
      showSnackbar(error.response?.data?.detail || 'Error saving manufacturer', 'error');
    }
  };

  const handleDelete = async (manufacturer) => {
    if (window.confirm(`Are you sure you want to delete "${manufacturer.name}"?`)) {
      try {
        await deleteManufacturer(manufacturer.id);
        showSnackbar('Manufacturer deleted successfully');
        fetchManufacturers();
      } catch (error) {
        console.error('Error deleting manufacturer:', error);
        showSnackbar(error.response?.data?.detail || 'Error deleting manufacturer', 'error');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      <TopNavbar />
      <PageContainer>
        <AdminPortalSideBar />
        <ContentContainer>
          <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Manufacturer Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
            >
              NEW
            </Button>
          </Box>

          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search manufacturers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{ maxWidth: 400 }}
            />
          </Box>

          {/* Manufacturers Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredManufacturers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No manufacturers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredManufacturers.map((manufacturer) => (
                    <TableRow key={manufacturer.id}>
                      <TableCell>{manufacturer.name}</TableCell>
                      <TableCell>{manufacturer.description || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ 
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: manufacturer.is_active ? '#e8f5e8' : '#ffebee',
                          color: manufacturer.is_active ? '#2e7d32' : '#d32f2f',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}>
                          {manufacturer.is_active ? 'Active' : 'Inactive'}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(manufacturer.created_at)}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleOpenDialog(manufacturer)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(manufacturer)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingManufacturer ? 'Edit Manufacturer' : 'Add New Manufacturer'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                name="name"
                label="Manufacturer Name"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    name="is_active"
                    color="primary"
                  />
                }
                label="Active"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained">
                {editingManufacturer ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        </ContentContainer>
      </PageContainer>
    </>
  );
}
